import React from 'react';
import {
  apiCancelProductPromotion,
  apiGetDepots,
  apiGetInputProduct,
  apiGetInputProducts,
  apiGetInputProductSubSubCategories,
} from 'constants/Api';
import {connect} from 'react-redux';
import {IFarmInputProduct, ISupplierUserProfile} from 'constants/interfaces';
import AddIcon from '@material-ui/icons/Add';
import {
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Typography,
} from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import {NavigationService} from 'services/index';
import {KhulaSupplierTable, Row} from 'components/Table/KhulaSupplierTable';
import {
  buildProductTableFilters,
  productTableHeaders,
  productTableRows,
} from 'screens/Dashboard/Products/ProductTableSetup';
import {ESidePanelView, ESupplierUserPermissions} from 'constants/index';
import {
  reduxSetDepots,
  reduxSetProducts,
  reduxSetSubSubCategories,
  reduxToggleSidePanel,
  reduxUpdateProduct,
} from 'appredux/actions';
import {ProductState} from 'appredux/reducers/reducer-products';
import moment from 'moment';
import {SubSubCategoryState} from 'appredux/reducers/reducer-sub-sub-categories';
import {PermissionsService} from 'services/PermissionsService';
import {DepotState} from 'appredux/reducers/reducer-depots';

type ProductsProps = {
  user: ISupplierUserProfile;
  productState: ProductState;
  subSubCategoriesState: SubSubCategoryState;
  depotState: DepotState;
};

/**
 * Component used to display products
 */
class Products extends React.Component<ProductsProps> {
  state: {
    isLoading: boolean;
    isRefreshing: boolean;
    showRefreshedSnackbar: boolean;
    productToRemove?: IFarmInputProduct;
    promotionProductToRemove?: IFarmInputProduct;
    isLoadingPromotionCancel: boolean;
  } = {
    isLoading: true,
    isRefreshing: false,
    showRefreshedSnackbar: false,
    productToRemove: undefined,
    promotionProductToRemove: undefined,
    isLoadingPromotionCancel: false,
  };

  componentDidMount() {
    const {productState} = this.props;
    const noProducts = !productState.products?.length;
    this.setState({isLoading: noProducts}, () => {
      this.loadProducts()
        .then(() => this.loadSubSubCategories())
        .then(() => this.loadDepots())
        .catch((err) => {
          alert('Error loading products. Please try again later');
          console.error(err);
        })
        .finally(() => this.setState({isLoading: false}));
    });
  }

  loadProducts(): Promise<void> {
    const {user, productState} = this.props;
    const {lastUpdate, products} = productState;

    const productsAreExpired = moment(lastUpdate).isBefore(
      moment().subtract(8, 'hours'),
    );
    const noProducts = !products?.length;

    if (productsAreExpired || noProducts)
      return apiGetInputProducts(user)
        .then((products) => reduxSetProducts(products))
        .then(() => {});

    return Promise.resolve();
  }

  loadSubSubCategories(): Promise<void> {
    const {subSubCategoriesState} = this.props;
    const {lastUpdate, subSubCategories} = subSubCategoriesState;

    const categoriesAreExpired = moment(lastUpdate).isBefore(
      moment().subtract(24, 'hours'),
    );
    const noCategories = !subSubCategories?.length;

    if (categoriesAreExpired || noCategories)
      return apiGetInputProductSubSubCategories()
        .then((categories) => reduxSetSubSubCategories(categories))
        .then(() => {});

    return Promise.resolve();
  }

  loadDepots(): Promise<void> {
    const {user, depotState} = this.props;
    const {lastUpdate, depots} = depotState;

    const depotsAreExpired = moment(lastUpdate).isBefore(
      moment().subtract(24, 'hours'),
    );
    const noDepots = !depots?.length;

    if (depotsAreExpired || noDepots)
      return apiGetDepots(user)
        .then((depots) => reduxSetDepots(depots))
        .then(() => {});

    return Promise.resolve();
  }

  refreshInputProducts() {
    const {user} = this.props;

    this.setState({isRefreshing: true}, () => {
      apiGetInputProducts(this.props.user)
        .then((products: IFarmInputProduct[]) => {
          reduxSetProducts(products);
          this.setState({
            isRefreshing: false,
            showRefreshedSnackbar: true,
          });
        })
        .then(() => apiGetInputProductSubSubCategories())
        .then((subSubCategories) => reduxSetSubSubCategories(subSubCategories))
        .then(() => apiGetDepots(user))
        .then((depots) => reduxSetDepots(depots))
        .catch((err) => {
          alert('Error refreshing products. Please try again later');
          console.error(err);
        })
        .finally(() => this.setState({isRefreshing: false}));
    });
  }

  addProduct() {
    const type = {
      panelType: ESidePanelView.ADD_PRODUCT,
      data: {},
    };
    reduxToggleSidePanel(true, type, false);
  }

  editProduct(product: IFarmInputProduct) {
    const type = {
      panelType: ESidePanelView.EDIT_PRODUCT,
      data: product,
    };
    reduxToggleSidePanel(true, type, false);
  }

  copyProduct(product: IFarmInputProduct) {
    const type = {
      panelType: ESidePanelView.COPY_PRODUCT,
      data: product,
    };
    reduxToggleSidePanel(true, type, false);
  }

  promoteProduct(product: IFarmInputProduct) {
    const type = {
      panelType: ESidePanelView.PROMOTE_PRODUCT,
      data: product,
    };
    reduxToggleSidePanel(true, type, false);
  }

  cancelPromotion(product: IFarmInputProduct) {
    this.setState({promotionProductToRemove: product});
  }

  confirmCancelPromotion(product: IFarmInputProduct) {
    const depots = new PermissionsService(this.props.user).getEditableDepots(
      this.props.depotState.depots,
    );
    const depotIds = depots.map((depot) => depot.id);
    this.setState({isLoadingPromotionCancel: true});
    apiCancelProductPromotion([product.id], depotIds)
      .then(() => apiGetInputProduct(product.id.toString()))
      .then((productUpdate) => reduxUpdateProduct(productUpdate))
      .catch((err) => {
        alert(
          'Unable to cancel promotion at the moment. Please try again later.',
        );
        console.error('Failed to cancel product promotion', err);
      })
      .finally(() => {
        this.setState({
          promotionProductToRemove: undefined,
          isLoadingPromotionCancel: false,
        });
      });
  }

  removeProduct(product: IFarmInputProduct) {
    this.setState({productToRemove: product});
  }

  confirmRemoveProduct(product: IFarmInputProduct) {
    new Promise((resolve) => setTimeout(resolve, 1000))
      .then(() => console.log('mock removed product', product))
      .then(() => {
        NavigationService.navigate('/');
        setTimeout(() => NavigationService.navigate('products'), 50);
      })
      .catch((err) => {
        alert(
          'Unable to remove product at the moment. Please try again later.',
        );
        console.error(err);
      })
      .finally(() => this.setState({productToRemove: undefined}));
  }

  render() {
    const {productState, subSubCategoriesState} = this.props;
    const {products, lastUpdate} = productState;
    const {subSubCategories} = subSubCategoriesState;
    const {
      isLoading,
      isRefreshing,
      showRefreshedSnackbar,
      productToRemove,
      promotionProductToRemove,
      isLoadingPromotionCancel,
    } = this.state;

    const permissions = new PermissionsService(this.props.user);
    const shouldOnlyShowActiveProducts = this.props.user.isCommodityGroup;
    const visibleProducts = shouldOnlyShowActiveProducts
      ? (products || []).filter((product) => product.isActive)
      : products || [];
    const visibleFilters = buildProductTableFilters(
      visibleProducts,
      subSubCategories,
      !shouldOnlyShowActiveProducts,
      !shouldOnlyShowActiveProducts,
    );
    const mapRowToProduct = (row: Row) =>
      // @ts-ignore
      visibleProducts.find((product) => product.id == row.id);

    const isProductsGlobalViewOnly =
      permissions.shouldShowProductsGlobalViewOnly();
    const canWriteProducts =
      permissions.hasPermission(ESupplierUserPermissions.PRODUCT_WRITE) &&
      !isProductsGlobalViewOnly;
    const canPromote =
      permissions.hasPermission(ESupplierUserPermissions.PRODUCT_PROMOTE) &&
      !isProductsGlobalViewOnly;
    const toolbarActions = canWriteProducts ? (
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={() => this.addProduct()}
        size="small"
        color={'primary'}
      >
        Add Product
      </Button>
    ) : undefined;

    return (
      <div className="table-screen products-container">
        <Typography variant="h4" className="page-title">
          Products
        </Typography>
        <Typography variant="body1" className="page-subtitle">
          Your company products
        </Typography>
        <div className="products-section">
          <KhulaSupplierTable
            headers={productTableHeaders({
              canPromote,
              canWrite: canWriteProducts,
            })}
            rows={productTableRows(
              visibleProducts,
              subSubCategories,
              (product) => this.editProduct(product),
              (product) => this.copyProduct(product),
              (product) => this.promoteProduct(product),
              (product) => this.cancelPromotion(product),
              canWriteProducts,
            )}
            defaultSortField={'name'}
            filtersGroups={visibleFilters}
            isLoading={isLoading}
            isRefreshing={isRefreshing}
            onRefresh={() => this.refreshInputProducts()}
            toolbarActions={toolbarActions}
            onRowSelect={(row: any) => {
              const product = mapRowToProduct(row);
              if (!product) return;
              this.editProduct(product);
            }}
          />
          <Box mt={1}>
            <Typography variant={'caption'}>
              Last updated: {moment(lastUpdate).fromNow()}
            </Typography>
          </Box>
          <Snackbar
            anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            open={showRefreshedSnackbar}
            onClose={() => this.setState({showRefreshedSnackbar: false})}
            autoHideDuration={3000}
            message="Products refreshed"
          />
          <RemoveProductDialog
            open={!!productToRemove}
            product={productToRemove}
            onConfirm={() => this.confirmRemoveProduct(productToRemove!)}
            onCancel={() => this.setState({productToRemove: undefined})}
          />
          <CancelPromotionDialog
            open={!!promotionProductToRemove}
            product={promotionProductToRemove}
            isLoading={isLoadingPromotionCancel}
            onConfirm={() =>
              this.confirmCancelPromotion(promotionProductToRemove!)
            }
            onCancel={() =>
              this.setState({promotionProductToRemove: undefined})
            }
          />
        </div>
      </div>
    );
  }
}

const RemoveProductDialog = (props: {
  open: boolean;
  product?: IFarmInputProduct;
  onConfirm: () => any;
  onCancel: () => any;
}) => {
  return (
    <Dialog open={props.open} onClose={props.onCancel}>
      <DialogTitle>Confirm Remove Product</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to remove {props.product?.name}? You will not be
          able to undo.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onCancel}>Cancel</Button>
        <Button onClick={props.onConfirm} className="warning-button" autoFocus>
          Remove
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CancelPromotionDialog = (props: {
  open: boolean;
  product?: IFarmInputProduct;
  isLoading: boolean;
  onConfirm: () => any;
  onCancel: () => any;
}) => {
  return (
    <Dialog open={props.open} onClose={props.onCancel}>
      <DialogTitle>Confirm Cancel Promotion</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to cancel the promotion for{' '}
          {props.product?.name}? You will not be able to undo.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onCancel}>Cancel</Button>
        <Button
          onClick={props.onConfirm}
          className="warning-button"
          disabled={props.isLoading}
        >
          {props.isLoading && (
            <CircularProgress size={20} thickness={6} color={'inherit'} />
          )}
          {!props.isLoading && 'Remove'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = (state: any) => {
  return {
    user: state.user,
    productState: state.products,
    subSubCategoriesState: state.subSubCategories,
    depotState: state.depots,
  };
};

export default connect(mapStateToProps)(Products);
